"use client";

import { useEffect, useRef, useState } from "react";
import * as Icons from "lucide-react";
import type { Locale } from "@/lib/i18n/config";
import type { NewsItem } from "@/lib/content/news";

/**
 * Multi-news strip:
 *  - Viser alle aktive nyheder ved siden af hinanden
 *  - Hvis indholdet overflower containeren: animér som ticker (newsticker)
 *  - Pause-on-hover så brugeren kan læse en specifik
 *  - Hvis indholdet er smalt nok: vis statisk uden animation
 */

type Props = {
  items: NewsItem[];
  locale: Locale;
};

const Icon = Icons as unknown as Record<
  string,
  React.ComponentType<{ size?: number; strokeWidth?: number; className?: string }> | undefined
>;

export function NewsTicker({ items, locale }: Props) {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [animDuration, setAnimDuration] = useState(40);

  // Mål den fulde bredde af én sets nyheder for at beregne en behagelig læse-hastighed
  useEffect(() => {
    const measure = () => {
      if (!trackRef.current) return;
      // Track indeholder DOUBLED items (for seamless loop) — vi måler én sets bredde
      const fullWidth = trackRef.current.scrollWidth / 2;
      // ~80px/sekund er behageligt for læsning, min 20s
      setAnimDuration(Math.max(20, Math.round(fullWidth / 80)));
    };

    measure();
    const ro = new ResizeObserver(measure);
    if (trackRef.current) ro.observe(trackRef.current);
    return () => ro.disconnect();
  }, [items.length]);

  return (
    <section
      data-dark
      aria-label="Nyheder"
      className="relative overflow-hidden text-white py-3.5 lg:py-4 group/ticker"
      style={{ background: "linear-gradient(135deg, var(--color-navy-700) 0%, #042639 100%)" }}
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: "radial-gradient(ellipse at 100% 0%, rgba(118,193,105,.2) 0%, transparent 55%)" }}
      />

      {/* Container er full-width — IKKE container-wide — så ticker fylder hele skærmens bredde */}
      <div className="relative overflow-hidden">
        <div
          ref={trackRef}
          className="flex items-center whitespace-nowrap newsticker-track"
          style={{ animationDuration: `${animDuration}s` }}
        >
          {/* Dobbel rendering for seamless loop */}
          {[...items, ...items].map((item, idx) => (
            <div key={`${item.id}-${idx}`} className="flex items-center flex-shrink-0">
              <NewsChip
                item={item}
                locale={locale}
                aria-hidden={idx >= items.length}
              />
              {/* Separator mellem chips */}
              <span aria-hidden="true" className="mx-2 lg:mx-4 w-px h-4 bg-white/15 flex-shrink-0" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function NewsChip({
  item,
  locale,
  ...rest
}: { item: NewsItem; locale: Locale } & React.HTMLAttributes<HTMLAnchorElement | HTMLDivElement>) {
  const tag = locale === "en" ? (item.tag_label_en ?? item.tag_label) : item.tag_label;
  const title = locale === "en" ? (item.title_en ?? item.title_da) : item.title_da;
  const body = locale === "en" ? (item.content_short_en ?? item.content_short_da) : item.content_short_da;

  const href =
    item.link_type === "internal"
      ? locale === "en" ? `/en/nyheder/${item.slug}` : `/nyheder/${item.slug}`
      : item.link_type === "external"
      ? item.link_url ?? null
      : null;

  const IconComponent = item.icon ? Icon[item.icon] : null;

  const content = (
    <>
      {tag && (
        <span className="bg-[var(--color-primary-800)] text-white text-[10px] font-bold uppercase tracking-[0.08em] px-2.5 py-1 rounded-[20px] flex-shrink-0 inline-flex items-center gap-1.5">
          {IconComponent && <IconComponent size={11} strokeWidth={2.5} />}
          {tag}
        </span>
      )}
      <span className="text-[14px] leading-relaxed text-white/90 whitespace-nowrap">
        <strong className="text-white font-bold">{title}</strong>
        {body && <span className="text-white/75"> · {body}</span>}
      </span>
    </>
  );

  // Vises som chip — fuld tekst, INGEN truncation. ticker'en sørger for at alt
  // kan læses ved at scrolle båndet.
  const chipClass = "inline-flex items-center gap-2 lg:gap-3 px-4 py-1.5 flex-shrink-0";
  const interactive = "hover:bg-white/5 transition-colors";

  if (href) {
    if (href.startsWith("http")) {
      return (
        <a href={href} target="_blank" rel="noopener noreferrer" className={`${chipClass} ${interactive}`} {...rest}>
          {content}
        </a>
      );
    }
    return (
      <a href={href} className={`${chipClass} ${interactive}`} {...rest}>
        {content}
      </a>
    );
  }
  return <div className={chipClass} {...(rest as React.HTMLAttributes<HTMLDivElement>)}>{content}</div>;
}
